import { Anchor, Button, Checkbox, ErrorText, Field, Label } from '@cluey/cluey-components';
import { useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import isError from 'lodash/isError';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { z } from 'zod';

import { PATHS, SIGNUP_ORIGIN, T_SIGNUP_ORIGIN, decodeToken, useSignupStore } from '.';
import { api } from '../../../../api';
import alertExclamation from '../../../../assets/images/icon-filled-alert-exclamation-circle.svg';
import withHeadTag from '../../../../common/HeadComponent';
import SelectDropdown from '../../../../components/SelectDropdown/SelectDropdown';
import env from '../../../../env';
import { ddLogger, ddLoggerLevel } from '../../../../logging';
import { isValidMobileNumber } from '../../../../util/validation/phoneNumber';
import RightColumn from '../RightColumn';
import TwoColumnLayout from '../TwoColumnLayout';
import { InlineLoader } from '../common';
import { BackButton } from '../common';
import useNavigateWithSearch from '../useNavigateWithSearch';
import Stepper from './Stepper';
import useSearchParam from './useSearchParam';
import useSignout from './useSignout';

const SignUpPageValidation = z.object({
  fname: z
    .string()
    .min(1, 'Please enter your first name.')
    .regex(/^[a-zA-Z\s0-9_\-']+$/, "Enter letters, numbers, spaces and symbols _-'. only"),
  lname: z
    .string()
    .min(1, 'Please enter your last name.')
    .regex(/^[a-zA-Z\s0-9_\-']+$/, "Enter letters, numbers, spaces and symbols _-'. only"),
  email: z.string().email('Please enter a valid email address.').min(1, 'Please enter your email address.'),
  mobileNumber: z
    .string()
    .min(1, 'Enter a valid mobile number')
    .refine((val) => isValidMobileNumber({ value: val }), 'Enter a valid mobile number.'),
  countryCode: z.string().min(1, 'Please select a country.'),
  agree: z.boolean().refine((v) => v, { message: 'Agree to receive communication to continue.' }),
});
type SignUpPageValidationType = z.infer<typeof SignUpPageValidation>;
type SignUpPageErrors = Record<keyof SignUpPageValidationType, string>;

type SignUpPageData = {
  title: string;
  reasons: string[];
  action: string;
};

type SIGNUP_VARIANT_KEYS = T_SIGNUP_ORIGIN | 'standard';
const SIGNUP_VARIANTS: Record<SIGNUP_VARIANT_KEYS, SignUpPageData> = {
  standard: {
    title: 'Sign up with Cluey Hub',
    reasons: [
      'Access 90+ worksheets tailored for years 2 to 10',
      "Practice 1000's of Mathematics questions",
      'Trial personalised learning programs',
    ],
    action: 'Sign up',
  },
  worksheets: {
    title: 'Download worksheets directly from Cluey Hub',
    reasons: ['Access 90+ worksheets tailored for years 2 to 10', "Practice 1000's of Mathematics questions"],
    action: 'Access worksheets now',
  },
  assessments: {
    title: 'Sign up with Cluey Hub',
    reasons: [
      'FREE assessment for years 3 to 9 students',
      'Choose between English and Mathematics',
      'See assessment score',
    ],
    action: 'Sign up',
  },
};
const SIGNUP_DEFAULT: SIGNUP_VARIANT_KEYS = 'assessments';

const countryOptions = [
  {
    text: 'Australia',
    value: 'AU',
  },
  {
    text: 'New Zealand',
    value: 'NZ',
  },
];

const SIGNUP_QUERY_KEYS_NOT_IN_TRACKING = new Set(['email', 'name', 'phoneNumber', 'countryCode']);

const splitFullNameFromParams = (name: string | undefined): { fname: string; lname: string } => {
  const trimmed = name?.trim() ?? '';
  if (!trimmed) {
    return { fname: '', lname: '' };
  }
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { fname: parts[0], lname: '' };
  }
  return { fname: parts.slice(0, -1).join(' '), lname: parts[parts.length - 1] ?? '' };
};

const renderErrorIcon = (message: string) => {
  return (
    <div className="flex flex-row items-start gap-1">
      <img src={alertExclamation} alt="error-icon" width={16} height={16} />
      <span className="text-sm font-bold !text-red-5">{message}</span>
    </div>
  );
};

const SignUpPageRaw = () => {
  const navigateWithSearch = useNavigateWithSearch();
  const location = useLocation<{ loginPageEmail: string }>();
  const params = new URLSearchParams(location.search);
  const tracking = Object.fromEntries(
    [...params.entries()].filter(([key]) => !SIGNUP_QUERY_KEYS_NOT_IN_TRACKING.has(key))
  );
  const userDetailsFromParams = Object.fromEntries(
    [...params.entries()].filter(([key]) => SIGNUP_QUERY_KEYS_NOT_IN_TRACKING.has(key))
  );
  const isOauthSignup = params.get('oauthSignup') === 'true';
  const oauthEmail = params.get('email');
  console.log('[debug] signup ', { isOauthSignup, oauthEmail });
  const { loginPageEmail } = location.state || {};
  const signout = useSignout();

  const [nameFromParams] = useState(() => splitFullNameFromParams(userDetailsFromParams.name));
  const [fname, setFname] = useState(nameFromParams.fname ?? '');
  const [lname, setLname] = useState(nameFromParams.lname ?? '');
  const [email, setEmail] = useState(() => userDetailsFromParams.email ?? loginPageEmail ?? '');
  const [mobileNumber, setMobileNumber] = useState(() => userDetailsFromParams.mobileNumber ?? '');
  const [countryCode, setCountryCode] = useState('AU');
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState<SignUpPageErrors>({
    fname: '',
    lname: '',
    email: '',
    mobileNumber: '',
    countryCode: '',
    agree: '',
  });
  const [submitError, setSubmitError] = useState('');

  const { mutate, isLoading: loading } = useMutation({
    mutationFn: async ({
      fname,
      lname,
      email,
      mobileNumber,
      countryCode,
    }: {
      fname: string;
      lname: string;
      email: string;
      mobileNumber: string;
      countryCode: string;
    }) => {
      const response = await fetch(
        `https://${env.REACT_APP_CLUEY_API_HOST_NAME}/customer-hub/customer/create/salesforce`,
        {
          method: 'POST',
          body: JSON.stringify({
            first_name: fname,
            last_name: lname,
            email,
            mobile_number: mobileNumber || '',
            country_code: countryCode,
            source: 'hub-sign-up',
            tracking,
          }),
        }
      );

      const data = await response.json();
      console.log('RESPONSE', data);
      useSignupStore.setState({ customerNumber: data.username });

      return z
        .object({
          message: z.literal('OK'),
          username: z.string(),
          token: z.string(),
        })
        .parse(data);
    },
    onSuccess: (data) => {
      useSignupStore.setState({
        username: data.username,
        email,
        token: data.token,
        mobileNumber,
      });
      navigateWithSearch(PATHS.VERIFY_PATH);
    },
    onError: (error) => {
      console.log('[debug] Signup error ', { error });
      if (isError(error)) {
        if ((error as Error).message.includes('already exists')) {
          setErrors((curr) => ({ ...curr, email: 'This email address is already in use.' }));
        } else {
          setSubmitError((error as Error).message);
        }
        ddLogger({
          level: ddLoggerLevel.ERROR,
          label: 'Sign up failed',
          data: { error: (error as Error).message, fname, lname, email },
          error,
        });
      }
    },
  });

  const origin = useSearchParam('origin');

  const pageInfo = useMemo(() => {
    const parsedOrigin = SIGNUP_ORIGIN.safeParse(origin);
    if (parsedOrigin.success) {
      useSignupStore.setState({ origin: parsedOrigin.data });
      return SIGNUP_VARIANTS[parsedOrigin.data];
    } else {
      return SIGNUP_VARIANTS[SIGNUP_DEFAULT];
    }
  }, [origin]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError('');
    setErrors({
      fname: '',
      lname: '',
      email: '',
      mobileNumber: '',
      agree: '',
      countryCode: '',
    });

    signout();
    api.account.details.resetQueries();

    const result = SignUpPageValidation.safeParse({ fname, lname, email, mobileNumber, agree, countryCode });

    if (result.success === true) {
      mutate({ fname, lname, email, mobileNumber, countryCode });
    } else {
      const errorFields = {
        fname: '',
        lname: '',
        email: '',
        mobileNumber: '',
        countryCode: '',
        agree: '',
      };

      result.error.errors.forEach((error) => {
        const field = error.path.join('.');
        errorFields[field] = error.message;
      });

      setErrors(errorFields);
    }
  };

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(location.search);

    if (urlSearchParams.has('token')) {
      const token = urlSearchParams.get('token');
      if (token) {
        const decoded = decodeToken(token);
        if (decoded) {
          useSignupStore.setState({
            token,
            email: decoded.email,
            tempPwd: 'tmpPassword' in decoded ? decoded.tmpPassword : decoded.temporaryPassword,
          });
          navigateWithSearch(PATHS.PASSWORD_PATH);
        } else {
          console.warn('Unable to decode token', token);
        }
      }
    }
  }, [location.search, navigateWithSearch]);

  const renderRightColumn = () => <RightColumn step={2} />;

  const isNZ = countryCode === 'NZ';
  const privacyPolicyLink = isNZ
    ? 'https://clueylearning.co.nz/privacy-policy/'
    : 'https://clueylearning.com.au/privacy-policy/';

  const renderLeftColumn = () => (
    <div title={pageInfo.title} className="ml-auto mr-auto box-content flex h-full max-w-[420px] flex-col px-5">
      <div className="mt-10">
        <BackButton />
      </div>
      <div className="mb-6 mt-6">
        <Stepper steps={5} activeStep={1} />
      </div>
      <h2 className="mb-4 font-display text-4xl font-bold">Let's get started</h2>
      <div className="font-bold">Start exploring for free. No credit card, no catch.</div>
      <form onSubmit={onSubmit} className="mt-10 grid w-full grid-cols-2 gap-x-4 gap-y-5">
        <Field
          id="signup-fname"
          label="First name"
          name="fname"
          value={fname}
          onChange={(e) => {
            setFname(e);
            setErrors({ ...errors, fname: '' });
          }}
          type="text"
          className="col-span-2 w-full sm:col-span-1"
          error={errors.fname}
          disabled={loading}
        />
        <Field
          id="signup-lname"
          label="Last name"
          name="lname"
          value={lname}
          onChange={(e) => {
            setLname(e);
            setErrors({ ...errors, lname: '' });
          }}
          type="text"
          className="col-span-2 w-full sm:col-span-1"
          error={errors.lname}
          disabled={loading}
        />
        <Field
          id="signup-email"
          label="Email"
          name="email"
          value={email}
          onChange={(e) => {
            setEmail(e);
            setErrors({ ...errors, email: '' });
          }}
          type="email"
          className="col-span-2"
          error={errors.email}
          disabled={loading || !!params.get('email') || !!loginPageEmail || !!oauthEmail}
        />
        <Field
          id="mobile-number"
          label="Mobile number"
          name="mobileNumber"
          value={mobileNumber}
          onChange={(e) => {
            setMobileNumber(e);
            setErrors({ ...errors, mobileNumber: '' });
          }}
          type="tel"
          className="col-span-2"
          error={errors.mobileNumber}
          disabled={loading}
        />

        <div className="flex w-full flex-col gap-1" data-error-field="countryCode">
          <Label id="country-code" size="base">
            Country
          </Label>
          <SelectDropdown
            options={countryOptions}
            value={countryCode}
            onChange={(value) => setCountryCode(value)}
            placeholder=""
            className="w-full"
            buttonClassName={classNames(
              'w-full text-left border border-grey-3 !px-3 rounded',
              errors.countryCode && '!bg-red-1 !border-red-5 !border-2'
            )}
            labelClassName="w-full !font-normal !text-base"
            iconFill="blue-5"
            optionClassName="!p-3"
          />
          {errors.countryCode}
        </div>
        <div className="col-span-2 mt-4">
          <div className="flex items-center gap-3 pb-1">
            <div className="h-12 w-12">
              <Checkbox
                disabled={loading}
                id="signup-agree"
                value={agree}
                error={!!errors.agree}
                onChange={(e) => {
                  setAgree(e);
                  setErrors({ ...errors, agree: '' });
                }}
              />
            </div>
            <label htmlFor="signup-agree">
              I agree to receive communication in line with Cluey's
              <Anchor className="ml-[0.25rem]" href={privacyPolicyLink} size="base" target="_blank">
                Privacy Policy
              </Anchor>
            </label>
          </div>
          {errors.agree && <ErrorText>{renderErrorIcon(errors.agree)}</ErrorText>}
        </div>
        {submitError && (
          <div className="mt-2">
            <ErrorText>{submitError}</ErrorText>
          </div>
        )}
        <Button
          size="lg"
          type="submit"
          disabled={loading}
          className="relative col-span-2 mb-10 flex w-full items-center justify-center"
        >
          <span className={loading ? 'opacity-0' : ''}>Continue</span>
          {loading && <InlineLoader className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />}
        </Button>
      </form>
    </div>
  );

  return <TwoColumnLayout renderLeftColumn={renderLeftColumn} renderRightColumn={renderRightColumn} />;
};

export const SignUpPage = withHeadTag(SignUpPageRaw);
